export type ZoomMode = 'hardware' | 'digital';

export interface ZoomRange {
  readonly min: number;
  readonly max: number;
  readonly step: number;
}

export const DIGITAL_ZOOM_RANGE: ZoomRange = { min: 1, max: 4, step: 0.1 };

const ZOOMED_THRESHOLD = 0.05;

interface ZoomCapability {
  min?: number;
  max?: number;
  step?: number;
}

export const hardwareZoomRange = (track: MediaStreamTrack): ZoomRange | null => {
  if (typeof track.getCapabilities !== 'function') {
    return null;
  }
  const zoom = (track.getCapabilities() as { zoom?: ZoomCapability }).zoom;
  if (!zoom || zoom.min === undefined || zoom.max === undefined || zoom.max <= zoom.min) {
    return null;
  }
  return { min: zoom.min, max: zoom.max, step: zoom.step || DIGITAL_ZOOM_RANGE.step };
};

export const currentHardwareZoom = (track: MediaStreamTrack, range: ZoomRange): number => {
  const settings = typeof track.getSettings === 'function' ? (track.getSettings() as { zoom?: number }) : {};
  return clampZoom(settings.zoom ?? range.min, range);
};

export const clampZoom = (level: number, range: ZoomRange): number => {
  const stepped = Math.round((level - range.min) / range.step) * range.step + range.min;
  return Number(Math.min(range.max, Math.max(range.min, stepped)).toFixed(2));
};

export const pinchZoom = (startLevel: number, startDistance: number, distance: number, range: ZoomRange): number =>
  startDistance > 0 ? clampZoom(startLevel * (distance / startDistance), range) : startLevel;

export const setHardwareZoom = async (track: MediaStreamTrack, level: number): Promise<void> => {
  await track.applyConstraints({ advanced: [{ zoom: level } as MediaTrackConstraintSet] });
};

export const isZoomed = (level: number, range: ZoomRange): boolean => level - range.min > ZOOMED_THRESHOLD;

export const zoomLabel = (level: number): string => `${level.toFixed(1)}x`;

export const cropToZoom = (image: string, zoom: number, quality: number): Promise<string> =>
  new Promise((resolve, reject) => {
    const source = new Image();
    source.onload = () => {
      const width = Math.round(source.naturalWidth / zoom);
      const height = Math.round(source.naturalHeight / zoom);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (context === null) {
        reject(new Error('canvas 2d context unavailable'));
        return;
      }
      context.drawImage(
        source,
        (source.naturalWidth - width) / 2,
        (source.naturalHeight - height) / 2,
        width,
        height,
        0,
        0,
        width,
        height
      );
      resolve(canvas.toDataURL('image/jpeg', quality));
    };
    source.onerror = () => reject(new Error('Could not decode image'));
    source.src = image;
  });
